import React, { useMemo } from 'react';
import moment from 'moment-timezone';
import classNames from 'classnames';
import styles from './MonthCalendarGrid.module.scss';

const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const buildWeekdayLabels = (weekStartsOn) =>
  [...Array(7)].map((_, i) => WEEKDAY_LABELS[(weekStartsOn + i) % 7]);

// Whole weeks only (no padding to a fixed 6 rows) — the grid naturally has
// 4-6 rows depending on the month and weekStartsOn.
const buildVisibleDates = (monthDate, weekStartsOn) => {
  const start = moment(monthDate).startOf('month');
  const end = moment(monthDate).endOf('month');

  const gridStart = start.clone();
  while (gridStart.day() !== weekStartsOn) {
    gridStart.subtract(1, 'day');
  }
  const lastDayOfWeek = (weekStartsOn + 6) % 7;
  const gridEnd = end.clone();
  while (gridEnd.day() !== lastDayOfWeek) {
    gridEnd.add(1, 'day');
  }

  const dates = [];
  const cursor = gridStart.clone();
  while (cursor.isSameOrBefore(gridEnd, 'day')) {
    dates.push(cursor.clone());
    cursor.add(1, 'day');
  }
  return dates;
};

export const MonthCalendarGridComponent = ({
  monthDate,
  getDayContent,
  weekStartsOn = 0,
  onDayClick,
  className,
}) => {
  const weekdayLabels = useMemo(
    () => buildWeekdayLabels(weekStartsOn),
    [weekStartsOn]
  );
  const visibleDates = useMemo(
    () => buildVisibleDates(monthDate, weekStartsOn),
    [monthDate, weekStartsOn]
  );
  const currentMonthNumber = moment(monthDate).month();
  const today = moment();

  return (
    <div className={classNames(styles.monthCalendarGrid, className)}>
      <div className={styles.weekdayRow}>
        {weekdayLabels.map((label) => (
          <div key={label} className={styles.weekdayLabel}>
            {label}
          </div>
        ))}
      </div>
      <div className={styles.dayGrid}>
        {visibleDates.map((dateMoment) => {
          const dateStr = dateMoment.format('YYYY-MM-DD');
          const isCurrentMonth = dateMoment.month() === currentMonthNumber;
          const isToday = dateMoment.isSame(today, 'day');
          const content = getDayContent
            ? getDayContent(dateStr, dateMoment)
            : null;

          return (
            <div
              key={dateStr}
              className={classNames(styles.dayCell, {
                [styles.dayCell_outside]: !isCurrentMonth,
                [styles.dayCell_today]: isToday,
                [styles.dayCell_clickable]: Boolean(onDayClick),
              })}
              onClick={onDayClick ? () => onDayClick(dateStr) : undefined}
            >
              <div className={styles.dayNumber}>{dateMoment.date()}</div>
              {content && <div className={styles.dayContent}>{content}</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
};

MonthCalendarGridComponent.displayName = 'MonthCalendarGrid';
